import React, { useState, useEffect, useCallback } from 'react'
import { connect, useDispatch } from 'react-redux';
import { Modal, Spinner, Button } from 'react-bootstrap';
import { Link } from "react-router-dom";
import {
    listSpecialitiesAction,
    startLoader,
    createProviderSpecialityAction,
    ProviderSpecialityFileUploadAction,
    deleteDataAction,
    updateSpecialityAction,
    downloadSpecialityTemplateAction // Added this action
} from '../../../../store/actions/ProviderActions';
import swal from 'sweetalert';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Button as PButton } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataTableBase } from '../../DataTables/DataTableBase';
import { currentUser } from "../../../../store/selectors/CurrentUserSelector";
import { useTranslation } from "react-i18next";

const ProviderSpecialite = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    
    // État initial simplifié
    const initialFormState = {
        nameFr: '',
        nameEn: '',
        code: ''
    };
    
    const initialErrorState = { name: '', code: '' };

    // États
    const [formData, setFormData] = useState(initialFormState);
    const [file, setFile] = useState(null);
    const [errors, setErrors] = useState(initialErrorState);
    const [providerSpecialityId, setProviderSpecialityId] = useState('');
    const [addCard, setAddCard] = useState(false);
    const [updateMode, setUpdateMode] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    // États des filtres
    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    // Helper pour mettre à jour le formData
    const updateFormField = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    // Fonctions utilitaires
    const initFilters = useCallback(() => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            nameFr: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
        });
        setGlobalFilterValue("");
    }, []);

    const resetForm = useCallback(() => {
        setFormData(initialFormState);
        setFile(null);
        setErrors(initialErrorState);
        setUpdateMode(false);
        setProviderSpecialityId('');
    }, []);

    const openCreateModal = useCallback(() => {
        resetForm();
        setAddCard(true);
    }, [resetForm]);

    const closeModal = useCallback(() => {
        resetForm();
        setAddCard(false);
    }, [resetForm]);

    // NEW: Handler for downloading the template
    const handleDownloadTemplate = useCallback(() => {
        dispatch(downloadSpecialityTemplateAction());
    }, [dispatch]);

    const renderHeader = useCallback(() => {
        return (
            <div className="d-flex justify-content-between align-items-center p-2">
                <PButton 
                    type="button" 
                    icon="pi pi-filter-slash"  
                    label={t('common.reinit')} 
                    outlined 
                    onClick={initFilters}
                    size="small"
                />
                <div className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        value={globalFilterValue} 
                        onChange={onGlobalFilterChange}  
                        placeholder={t('common.search')}
                        size="small"
                    />
                </div>
            </div>
        );
    }, [globalFilterValue, initFilters, t]);

    const setColumns = useCallback(() => [
        { 
            name: 'nameFr', 
            headerName: t('common.name'), 
            filterPlaceholder: t('common.filter-name'), 
            style: { minWidth: '12rem' }, 
            filter: true, 
            bodyTemplate: null
        },
        { 
            name: 'createdAt', 
            filterField: 'createdAt', 
            dataType: 'date', 
            headerName: t("common.created-on"), 
            filterPlaceholder: t('facturation.create-date-filter'), 
            style: { minWidth: '6rem' }, 
            filter: true, 
            bodyTemplate: datePaiementBodyTemplate, 
            filterElement: dateFilterTemplate
        },
        { 
            name: 'actions', 
            headerName: 'Actions', 
            filterPlaceholder: '', 
            style: { minWidth: '8rem' }, 
            filter: false, 
            bodyTemplate: getActionsTemplate
        },
    ], [t]);

    const onGlobalFilterChange = useCallback((e) => {
        const value = e.target.value;
        
        if (!filters) {
            setGlobalFilterValue(value);
            return;
        }
        
        let _filters = { ...filters };
        
        // Additional safety check to ensure 'global' exists
        if (_filters['global']) {
            _filters['global'].value = value;
        } else {
            // If for some reason 'global' doesn't exist, create it
            _filters['global'] = { value: value, matchMode: FilterMatchMode.CONTAINS };
        }
        
        setFilters(_filters);
        setGlobalFilterValue(value);
    }, [filters]);

    const getActionsTemplate = useCallback((rowData) => {
        return (
            <div className="d-flex justify-content-center gap-1">
                {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'UPDATESPECIALITE') && (
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => updateData(rowData)}
                        title="Modifier"
                    >
                        <i className="fa fa-pencil"></i>
                    </button>
                )}
                {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'DELETEMEDICAMENT') && (
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(rowData)}
                        title="Supprimer"
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                )}
            </div>
        );
    }, []);

    const handleDelete = useCallback((rowData) => {
        swal({
            title: t("provider.delete-speciality") + rowData.nameFr + " ?",
            text: t("patient.delete-speciality"),
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                const data = { type: "SPECIALITE", id: rowData.id };
                dispatch(startLoader());
                dispatch(deleteDataAction(data));
            }
        });
    }, [t, dispatch]);

    const updateData = useCallback((data) => {
        setFormData({
            nameFr: data.nameFr || '',
            nameEn: data.nameEn || '',
            code: data.code || ''
        });
        setProviderSpecialityId(data.id);
        setUpdateMode(true);
        setAddCard(true);
    }, []);

    const dateFilterTemplate = useCallback((options) => {
        return (
            <Calendar 
                value={options.value} 
                onChange={(e) => options.filterCallback(e.value, options.index)} 
                dateFormat="dd/mm/yy" 
                placeholder="dd/mm/yyyy" 
                mask="99/99/9999" 
            />
        );
    }, []);

    const datePaiementBodyTemplate = useCallback((rowData) => {
        return formatDate(rowData.createdAt);
    }, []);

    const formatDate = useCallback((value) => {
        return value.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }, []);

    // Permissions
    const canDo = useCallback((menuCode, actionCode) => {
        const theMenu = props.currentUser.menus?.find(menu => menu.code === menuCode);
        return theMenu ? theMenu.actionMenus?.some(action => action.code === actionCode) : false;
    }, [props.currentUser.menus]);
    
    const hasMenu = useCallback((menuCode) => {
        return props.currentUser.menus?.some(menu => menu.code === menuCode) || false;
    }, [props.currentUser.menus]);

    // Effects
    useEffect(() => {
        const initializeData = async () => {
            setInitialLoading(true);
            initFilters();
            
            try {
                dispatch(startLoader());
                await dispatch(listSpecialitiesAction());
            } catch (error) {
                console.error('Erreur lors du chargement initial:', error);
            } finally {
                setInitialLoading(false);
            }
        };
        
        initializeData();
    }, [dispatch, initFilters]);

    useEffect(() => {
        resetForm();
        setAddCard(false);
    }, [props.specialities, resetForm]);

    // Sauvegarde
    const onProviderTypeSave = useCallback((e) => {
        e.preventDefault();
        let error = false;
        const errorObj = { ...initialErrorState };
        
        if (file) {
            dispatch(startLoader());
            dispatch(ProviderSpecialityFileUploadAction(file));
            return;
        }
        
        if (!formData.nameFr.trim()) {
            errorObj.name = t('common.name-required');
            error = true;
        }
        
        setErrors(errorObj);
        
        if (error) {
            swal('Oops', t('common.check-form-fields'), "error");
            return;
        }
        
        dispatch(startLoader());
        
        const payload = {
            nameFr: formData.nameFr,
            nameEn: formData.nameFr,
            code: formData.code
        };

        if (updateMode) {
            dispatch(updateSpecialityAction(providerSpecialityId, payload));
        } else {
            dispatch(createProviderSpecialityAction(payload));
        }
        
        closeModal();
    }, [formData, file, updateMode, providerSpecialityId, t, dispatch, closeModal]);

    const selectFile = useCallback((event) => {
        setFile(event.target.files[0]);
    }, []);

    return (
        <>
            {/* Header with download and add buttons */}
            <div className="provider-type-header mb-0 form-head bg-white p-3 bt-5 border-top-solid">
                <div>
                    <h4 className="mb-1">{t("provider.speciality")}</h4>
                    <p className="text-muted mb-0 small">{t("common.manage-specialities")}</p>
                </div>
                <div className="d-flex gap-2">
                    {/* NEW: Download Template Button */}
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleDownloadTemplate}
                    >
                        <i className="fa fa-download me-1"></i> {t("common.download-template")}
                    </button>
                    
                    {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'CREATESPECIALITE') && (
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={openCreateModal}
                        >
                            <i className="fa fa-plus-circle me-1"></i> {t("provider.new-speciality")}
                        </button>
                    )}
                </div>
            </div>
            
            {/* Modal propre et simple */}
            <Modal 
                show={addCard} 
                onHide={closeModal}
                size="lg"
                backdrop="static"
            >
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title className="h5">
                        {updateMode ? t("provider.edit-speciality") : t("provider.create-speciality")}
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body className="p-4">
                    <form onSubmit={onProviderTypeSave}>
                        {/* Section 1: Informations de base */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <h6 className="text-primary border-bottom pb-2 mb-3">{t("common.basic-info")}</h6>
                            </div>
                            
                            <div className="col-12 mb-3">
                                <label className="form-label">{t("common.name")} *</label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                    placeholder={t("provider.speciality-example")}
                                    value={formData.nameFr}
                                    disabled={props.showLoading || file}
                                    required={!file}
                                    onChange={(e) => updateFormField('nameFr', e.target.value)}
                                />
                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                            </div>
                        </div>

                        {/* Section 2: Import CSV */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <h6 className="text-primary border-bottom pb-2 mb-3">{t("common.import-csv")}</h6>
                            </div>
                            
                            <div className="col-12">
                                <div className="border rounded p-3 bg-light">
                                    <p className="text-muted mb-3 small">
                                        {t("provider.import-csv-desc")}
                                    </p>
                                    <div className="input-group">
                                        <span className="input-group-text">CSV</span>
                                        <input 
                                            type="file" 
                                            className="form-control" 
                                            onChange={selectFile}
                                            accept=".csv"
                                        />
                                    </div>
                                    {file && (
                                        <div className="alert alert-info mt-2 mb-0 py-2">
                                            <small>{t("common.selected-file")}: <strong>{file.name}</strong></small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </Modal.Body>

                <Modal.Footer className="border-top">
                    <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={closeModal}
                        disabled={props.showLoading}
                    >
                        {t("common.cancel")}
                    </button>
                    
                    {props.showLoading ? (
                        <Button variant="primary" disabled>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            <span className="ms-2">{t("common.saving")}</span>
                        </Button>
                    ) : (
                        <button 
                            type="submit" 
                            className="btn btn-primary"
                            onClick={onProviderTypeSave}
                        >
                            {updateMode ? t("common.update") : t("common.save")}
                        </button>
                    )}
                </Modal.Footer>
            </Modal>
            
            {/* Tableau simple et propre */}
            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    <DataTableBase 
                        data={props.specialities.length > 0 ? props.specialities.map(acte => {
                                acte.createdAt = new Date(acte.createdAt);
                                return acte;
                        }) : []}
                        emptyMessage={t("common.no-spe")}
                        filters={filters}
                        globalFiltersFields={['nameFr', 'createdAt']}
                        header={renderHeader()}
                        columns={setColumns()}
                        exportColumns={[
                            { title: t('common.name'), dataKey: 'nameFr' },
                            { title: t('common.create-date'), dataKey: 'createdAt' },
                        ]}
                        rows={10} 
                        showExport={true}
                        exportFilename={"specialites"}
                        loading={props.showLoading || initialLoading}
                        lazy={false}
                    />
                </div>
            </div>
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        specialities: state.provider.specialities,
        errorMessage: state.clientAuth.errorMessage,
        successMessage: state.clientAuth.successMessage,
        showLoading: state.provider.showLoading,
        currentUser: currentUser(state)
    };
};

export default connect(mapStateToProps)(ProviderSpecialite);